import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';

const reasons = [
  {
    num: '01',
    title: 'Instant Capacity',
    bold: 'Add production power whenever you need it.',
    description:
      'Plug-and-play designers, writers, SEO specialists, engineers, and AI operators—ready in minutes, working under your brand.',
    checks: [
      'Available in 48 hours or less',
      'Works inside your existing processes',
      'Scales up or down with demand',
    ],
  },
  {
    num: '02',
    title: 'New Capabilities Overnight',
    bold: 'Offer AI-powered services without hiring an AI team.',
    description:
      'Automation, agents, workflows, prototypes, data tasks—delivered by AI-native talent you can white-label and resell.',
    checks: [
      'Launch new service lines without new hires',
      'AI execution delivered under your brand',
      'Resellable, billable, profitable',
    ],
  },
  {
    num: '03',
    title: 'Faster Delivery at Lower Cost',
    bold: 'Get 2–5x faster output with hybrid human+AI execution.',
    description:
      'Your clients get results faster. You get better margins. Everyone wins—and you still maintain full creative control.',
    checks: [
      '2–5x faster delivery cycles',
      'Better margins on every project',
      'Senior-level quality, consistent output',
    ],
  },
];

export const WhySection = () => (
  <section className="py-24 md:py-32 bg-background border-t border-border">
    <div className="section-container">
      {/* Header */}
      <motion.div
        className="mb-16"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <p className="section-label mb-5">Why HireX8?</p>
        <h2 className="text-[42px] sm:text-[52px] font-extrabold tracking-tighter text-foreground leading-none">
          Scale Your Agency<br />
          <span className="text-apple-grey font-semibold">Without Scaling Headcount.</span>
        </h2>
      </motion.div>

      {/* Swiss numbered reasons */}
      <div className="flex flex-col gap-0">
        {reasons.map((r, i) => (
          <motion.div
            key={r.num}
            className="grid md:grid-cols-[100px_1fr_1fr] gap-6 md:gap-10 py-10 border-t border-border relative"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.6 }}
          >
            {/* Number */}
            <div className="flex items-start">
              <span className="text-[60px] font-black text-foreground/[0.07] leading-none tracking-tighter select-none">
                {r.num}
              </span>
            </div>

            {/* Content */}
            <div>
              <h3 className="text-[22px] font-bold text-foreground tracking-tight mb-3 leading-snug">
                {r.title}
              </h3>
              <p className="text-[15px] font-semibold text-foreground mb-2 leading-snug">{r.bold}</p>
              <p className="text-[15px] text-muted-foreground leading-[1.6]">{r.description}</p>
            </div>

            {/* Checks */}
            <ul className="space-y-3">
              {r.checks.map(c => (
                <li key={c} className="flex items-start gap-2.5">
                  <CheckCircle2 size={15} className="text-apple-blue flex-shrink-0 mt-0.5" />
                  <span className="text-[14px] text-muted-foreground leading-snug">{c}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
        <div className="swiss-divider" />
      </div>
    </div>
  </section>
);
